/* eslint-disable class-methods-use-this */
import { EventBus, Events } from "jiffy-store";
import { isMobile } from "common/utils/browser";
import "./product-color-palette.scss";

class ProductColorPalette {
  #colorItemClassName = ".js-product-color-palette-color-item";

  #activeItemClass = "js-color-variant-selector-active";

  #activeItemStyleClass = "product-color-palette__color-item--active";

  #hoverPreviewClass = "product-color-palette__color-item--hover-preview";

  #$root = null;

  #$colorItemActive = null;

  #$expandableContent = null;

  #$expandTrigger = null;

  #$lowStockBadge = null;

  #$materials = null;

  #$oosBadge = null;

  #$colorNameDisplay = null;

  #originalColorName = null;

  #isHovering = false;

  #hoverDebounceTimer = null;

  #$currentHoveredItem = null;

  get #contentId() {
    const id = this.#$expandTrigger?.getAttribute("aria-controls");
    return `#${id}`;
  }

  constructor() {
    this.#$root = document.querySelector(".js-product-color-palette");
    this.#$colorItemActive = this.#$root?.querySelector(".js-color-variant-selector-active");
    this.#$expandTrigger = this.#$root?.querySelector(".js-product-color-palette-expand-trigger");
    this.#$expandableContent = this.#$root?.querySelector(this.#contentId);
    this.#$lowStockBadge = this.#$root?.querySelector(".js-color-palette-low-stock-badge");
    this.#$materials = this.#$root?.querySelector(".js-color-palette-materials");
    this.#$oosBadge = this.#$root?.querySelector(".js-color-palette-oos-badge");
    this.#$colorNameDisplay = this.#$root?.querySelector(".js-current-color-name");
    this.#originalColorName = this.#$colorNameDisplay?.textContent?.trim()?.toLowerCase();
    if (this.#$colorNameDisplay && this.#originalColorName) {
      this.#$colorNameDisplay.textContent = this.#originalColorName;
    }
  }

  #checkOverflow = () => {
    const hasOverflow =
      this.#$expandableContent.scrollHeight > this.#$expandableContent.clientHeight;
    this.#$expandTrigger.classList.toggle("hidden", !hasOverflow);
  };

  #isActiveElementVisible = () => {
    const containerRect = this.#$expandableContent.getBoundingClientRect();
    const containerTop = this.#$expandableContent.scrollTop;
    const containerBottom = containerTop + this.#$expandableContent.clientHeight;
    const elementRect = this.#$colorItemActive.getBoundingClientRect();
    const elementTop = elementRect.top - containerRect.top + this.#$expandableContent.scrollTop;
    const elementBottom = elementTop + elementRect.height;
    return elementTop >= containerTop && elementBottom <= containerBottom;
  };

  #isExpanded = () => this.#$expandTrigger.getAttribute("aria-expanded") === "true";

  #getMaterials = materialsRaw => {
    let materials = [];
    try {
      materials = JSON.parse(materialsRaw);
    } catch (error) {
      window.Honeybadger?.notify(`[JS] Failed to parse materials JSON: ${error.message}`, {
        context: { materialsRaw }
      });
    }

    return materials.map(material => {
      const $material = document.createElement("div");
      $material.className = "product-color-palette__material";
      $material.textContent = material;
      return $material;
    });
  };

  #$getColorItemByCompositionId = compositionId =>
    this.#$root.querySelector(
      `${this.#colorItemClassName}[data-composition-id="${compositionId}"]`
    );

  #handleExpandTriggerClick = () => {
    const isExpanded = this.#isExpanded();

    this.#$expandTrigger.setAttribute("aria-expanded", !isExpanded);
    this.#$expandableContent.classList.toggle(
      "product-color-palette__content--expanded",
      !isExpanded
    );
  };

  #deactivateCurrentItem = () => {
    if (this.#$colorItemActive) {
      this.#$colorItemActive.classList.remove(this.#activeItemClass);
      this.#$colorItemActive.classList.remove(this.#activeItemStyleClass);
    }
  };

  #activateItem = $colorItem => {
    $colorItem.classList.add(this.#activeItemClass);
    $colorItem.classList.add(this.#activeItemStyleClass);
    this.#$colorItemActive = $colorItem;
  };

  #handleColorChange = ({ target: $target }) => {
    const $colorItem =
      $target.tagName === "A" ? $target : $target.closest(this.#colorItemClassName);

    const { color, lowStock, materials: materialsRaw, oos } = $colorItem.dataset;
    const materials = this.#getMaterials(materialsRaw);

    this.#$lowStockBadge.classList.toggle("hidden", lowStock !== "true");
    this.#$materials.replaceChildren(...materials);
    this.#$oosBadge.classList.toggle("hidden", oos !== "true");

    if ($colorItem !== this.#$colorItemActive) {
      this.#deactivateCurrentItem();
      this.#activateItem($colorItem);
    }
    if (color) this.#originalColorName = color.toLowerCase();
  };

  #resetToOriginalState = () => {
    this.#$currentHoveredItem = null;
    this.#isHovering = false;
    if (this.#$colorNameDisplay && this.#originalColorName) {
      this.#$colorNameDisplay.textContent = this.#originalColorName;
    }
    EventBus.publish(Events.PRODUCT_COLOR_PREVIEW_END, this, {});
  };

  #applyPreviewState = $colorItem => {
    $colorItem.classList.add(this.#hoverPreviewClass);
    this.#$currentHoveredItem = $colorItem;
    this.#isHovering = true;

    const { color, compositionId, previewImageUrl } = $colorItem.dataset;

    if (this.#$colorNameDisplay && color) {
      this.#$colorNameDisplay.textContent = color.toLowerCase();
    }

    EventBus.publish(Events.PRODUCT_COLOR_PREVIEW, this, { color, compositionId, previewImageUrl });
  };

  #handleColorHover = e => {
    const $colorItem = e.target.closest(this.#colorItemClassName);
    if (!$colorItem || $colorItem === this.#$currentHoveredItem) return;

    const isActiveItem = $colorItem === this.#$colorItemActive;
    if (isActiveItem && !this.#$currentHoveredItem) return;

    if (this.#hoverDebounceTimer) {
      clearTimeout(this.#hoverDebounceTimer);
    }

    this.#hoverDebounceTimer = setTimeout(() => {
      if (this.#$currentHoveredItem) {
        this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
      }

      if (isActiveItem) {
        this.#resetToOriginalState();
        return;
      }

      this.#applyPreviewState($colorItem);
    }, 30);
  };

  #handlePaletteMouseLeave = () => {
    if (this.#hoverDebounceTimer) {
      clearTimeout(this.#hoverDebounceTimer);
      this.#hoverDebounceTimer = null;
    }
    if (this.#$currentHoveredItem) {
      this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
      this.#resetToOriginalState();
    }
  };

  #initHoverListeners() {
    if (isMobile()) return;
    const $colorItems = this.#$root?.querySelectorAll(this.#colorItemClassName);
    if (!$colorItems) return;
    $colorItems.forEach($item => $item.addEventListener("mouseenter", this.#handleColorHover));
    this.#$expandableContent?.addEventListener("mouseleave", this.#handlePaletteMouseLeave);
  }

  #initEventListeners() {
    this.#$expandTrigger.addEventListener("click", () => {
      this.#handleExpandTriggerClick();
    });
    EventBus.on(Events.PRODUCT_COLOR_SELECTED, (event, { compositionId }) => {
      if (this.#hoverDebounceTimer) {
        clearTimeout(this.#hoverDebounceTimer);
        this.#hoverDebounceTimer = null;
      }
      if (this.#$currentHoveredItem) {
        this.#$currentHoveredItem.classList.remove(this.#hoverPreviewClass);
        this.#$currentHoveredItem = null;
      }
      const $colorItem = this.#$getColorItemByCompositionId(compositionId);
      this.#handleColorChange({ target: $colorItem });
    });
    window.addEventListener("resize", () => {
      if (this.#isExpanded()) return;
      this.#checkOverflow();
    });
  }

  init() {
    if (!this.#$root) return;
    this.#checkOverflow();
    this.#initEventListeners();
    this.#initHoverListeners();
    if (window.innerHeight >= 1000) this.#handleExpandTriggerClick();
    if (!this.#isActiveElementVisible()) this.#handleExpandTriggerClick();
  }
}

export default () => {
  const productColorPalette = new ProductColorPalette();
  productColorPalette.init();
};
